import type {
  AssetHealthOverview,
  MaintenanceInsuranceDue,
  AuditStatus,
  ExceptionAlerts,
  LifecycleStage,
  AssetAgeAnalysis,
  RecentAssetActivity,
} from '@/types/asset-management-widgets';
import { getBackendUrl } from '@/hooks/use-get-backend-url';
import { getModuleMode } from '@/lib/feature-mode';
import { getAuthConfig } from '@/lib/auth-fetch';
import { getApiUrl } from '@/lib/api-config';

// Helper function to get current data source mode from feature settings
const getDataSource = (): 'demo' | 'api' => {
  const mode = getModuleMode('dashboard');
  if (mode === 'demo') return 'demo';
  return 'api';
};

// Mock data for demo mode
const mockAssetHealthOverview: AssetHealthOverview = {
  totalAssets: 156,
  statusBreakdown: [
    { status: 'Active', count: 98, percentage: 62.8, color: '#10b981' },
    { status: 'In Use', count: 32, percentage: 20.5, color: '#3b82f6' },
    { status: 'Maintenance', count: 12, percentage: 7.7, color: '#f59e0b' },
    { status: 'Retired', count: 8, percentage: 5.1, color: '#6b7280' },
    { status: 'Disposed', count: 6, percentage: 3.9, color: '#ef4444' },
  ],
  healthDistribution: [
    { status: 'Excellent', count: 68, percentage: 43.6, color: '#10b981' },
    { status: 'Good', count: 52, percentage: 33.3, color: '#3b82f6' },
    { status: 'Fair', count: 24, percentage: 15.4, color: '#f59e0b' },
    { status: 'Poor', count: 8, percentage: 5.1, color: '#ef4444' },
    { status: 'Critical', count: 4, percentage: 2.6, color: '#991b1b' },
  ],
  alertCount: 14,
  maintenanceDue: 8,
  warrantyExpiring: 12,
  healthScore: 87,
};

const mockMaintenanceInsuranceDue: MaintenanceInsuranceDue = {
  items: [
    { assetId: '1', assetTag: 'AST-001', assetName: 'Dell Laptop XPS 15', type: 'Insurance', dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), priority: 'High', isOverdue: false, daysUntilDue: 7, description: 'Insurance renewal due' },
    { assetId: '2', assetTag: 'AST-002', assetName: 'Office Printer HP LaserJet', type: 'Maintenance', dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(), priority: 'Medium', isOverdue: false, daysUntilDue: 14, description: 'Scheduled maintenance' },
    { assetId: '3', assetTag: 'AST-003', assetName: 'Company Vehicle - Ford Transit', type: 'Insurance', dueDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), priority: 'High', isOverdue: true, daysUntilDue: -2, description: 'Insurance expired' },
    { assetId: '4', assetTag: 'AST-004', assetName: 'Server Rack Unit 1', type: 'Maintenance', dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), priority: 'Low', isOverdue: false, daysUntilDue: 30, description: 'Annual maintenance check' },
  ],
  totalCount: 4,
  overdueCount: 1,
  dueSoonCount: 2,
};

// Dynamic mock data function that uses actual demo data
const getMockAuditStatus = (): AuditStatus => {
  // Import demo data service
  const { demoDataService } = require('./demo');
  const data = demoDataService.getData();
  
  // Filter active audits (matching the Active Audits page filter)
  const activeAudits = data.audits.filter((a: any) => 
    a.status === 'draft' ||
    a.status === 'assigned' || 
    a.status === 'in-progress' || 
    a.status === 'under-review' ||
    a.status === 'on-hold' ||
    a.status === 'scheduled'
  );
  
  // Calculate average completion
  const totalCompletion = activeAudits.reduce((sum: number, a: any) => sum + (a.completionPercentage || 0), 0);
  const averageCompletion = activeAudits.length > 0 ? totalCompletion / activeAudits.length : 0;
  
  // Count overdue audits
  const now = new Date();
  const itemsOverdue = activeAudits.filter((a: any) => 
    a.scheduledEndDate && new Date(a.scheduledEndDate) < now && 
    a.status !== 'completed' && a.status !== 'cancelled'
  ).length;
  
  // Count exceptions this week
  const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const exceptionsThisWeek = data.exceptions.filter((e: any) => 
    new Date(e.createdDate) >= oneWeekAgo && e.status !== 'resolved'
  ).length;
  
  // Count completed audits this month
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const completedThisMonth = data.audits.filter((a: any) => 
    a.status === 'completed' && 
    a.actualEndDate && 
    new Date(a.actualEndDate) >= firstDayOfMonth
  ).length;
  
  // Map to widget format (top 4 audits)
  const mappedAudits = activeAudits.slice(0, 4).map((a: any) => ({
    auditId: a.id,
    auditName: a.title,
    status: a.status,
    totalAssets: a.totalAssets || 0,
    completedAssets: a.completedAssets || 0,
    completionPercentage: a.completionPercentage || 0,
    dueDate: a.scheduledEndDate,
    isOverdue: a.scheduledEndDate ? new Date(a.scheduledEndDate) < now : false,
  }));
  
  return {
    activeAudits: mappedAudits,
    totalActiveAudits: activeAudits.length,
    averageCompletion: Math.round(averageCompletion * 10) / 10,
    itemsOverdue,
    exceptionsThisWeek,
    completedThisMonth,
  };
};

// Dynamic mock data function that uses actual demo data
const getMockExceptionAlerts = (limit: number = 10): ExceptionAlerts => {
  // Import demo data service
  const { demoDataService } = require('./demo');
  const data = demoDataService.getData();
  
  // Filter open exceptions
  const openExceptions = data.exceptions.filter((e: any) => e.status !== 'resolved');
  
  // Map to widget format
  const mappedAlerts = openExceptions.slice(0, limit).map((e: any) => ({
    id: e.id,
    type: e.type,
    severity: e.severity,
    message: e.description || e.notes || `${e.type} exception`,
    assetId: e.assetId,
    assetTag: e.assetCode,
    assetName: e.assetName,
    createdDate: e.createdDate,
    isDismissed: false,
  }));
  
  // Count by severity
  const criticalCount = openExceptions.filter((e: any) => e.severity === 'critical').length;
  const warningCount = openExceptions.filter((e: any) => e.severity === 'high' || e.severity === 'medium').length;
  
  return {
    alerts: mappedAlerts,
    totalCount: openExceptions.length,
    criticalCount,
    warningCount,
  };
};

const mockLifecycleStage: LifecycleStage = {
  stages: [
    { stage: 'New', count: 24, percentage: 15.4, color: '#10b981' },
    { stage: 'Active', count: 68, percentage: 43.6, color: '#3b82f6' },
    { stage: 'Mature', count: 38, percentage: 24.4, color: '#f59e0b' },
    { stage: 'Aging', count: 18, percentage: 11.5, color: '#f97316' },
    { stage: 'End of Life', count: 8, percentage: 5.1, color: '#ef4444' },
  ],
  totalAssets: 156,
};

const mockAssetAgeAnalysis: AssetAgeAnalysis = {
  ageBuckets: [
    { label: '0-1 years', minAge: 0, maxAge: 1, count: 24, percentage: 15.4 },
    { label: '1-3 years', minAge: 1, maxAge: 3, count: 52, percentage: 33.3 },
    { label: '3-5 years', minAge: 3, maxAge: 5, count: 45, percentage: 28.8 },
    { label: '5-10 years', minAge: 5, maxAge: 10, count: 28, percentage: 17.9 },
    { label: '10+ years', minAge: 10, maxAge: 999, count: 7, percentage: 4.5 },
  ],
  averageAge: 3.8,
  totalAssets: 156,
};

const mockRecentAssetActivity: RecentAssetActivity = {
  activities: [
    { id: '1', activityType: 'Created', assetId: '10', assetTag: 'AST-010', assetName: 'New Dell Monitor 27"', description: 'Asset AST-010 was created', activityDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), userName: 'John Smith' },
    { id: '2', activityType: 'Updated', assetId: '5', assetTag: 'AST-005', assetName: 'MacBook Pro 16"', description: 'Asset AST-005 was updated', activityDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), userName: 'Jane Doe' },
    { id: '3', activityType: 'StatusChanged', assetId: '3', assetTag: 'AST-003', assetName: 'Company Vehicle - Ford Transit', description: 'Status changed from Active to Maintenance', activityDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), userName: 'Mike Johnson', oldValue: 'Active', newValue: 'Maintenance' },
    { id: '4', activityType: 'Created', assetId: '11', assetTag: 'AST-011', assetName: 'Ergonomic Office Chair', description: 'Asset AST-011 was created', activityDate: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(), userName: 'Sarah Wilson' },
    { id: '5', activityType: 'Transferred', assetId: '7', assetTag: 'AST-007', assetName: 'HP Laptop EliteBook', description: 'Asset transferred to Manchester Office', activityDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), userName: 'Tom Brown', oldValue: 'London HQ', newValue: 'Manchester Office' },
  ],
  totalCount: 5,
};

export class AssetManagementWidgetsService {
  private getBaseUrl(): string {
    return getApiUrl() ?? getBackendUrl();
  }

  private async buildHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const authConfig = getAuthConfig();
    if (authConfig.getAccessToken) {
      try {
        const token = await authConfig.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error getting access token:', error);
      }
    }

    headers['X-Instance-Id'] = authConfig.instanceId ?? 'default-instance';
    return headers;
  }

  async getAssetHealthOverview(): Promise<AssetHealthOverview> {
    if (getDataSource() === 'demo') {
      return mockAssetHealthOverview;
    }
    const headers = await this.buildHeaders();
    const response = await fetch(
      `${this.getBaseUrl()}/api/dashboard/widgets/asset-management/health-overview`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch asset health overview');
    return response.json();
  }

  async getMaintenanceInsuranceDue(limit: number = 10): Promise<MaintenanceInsuranceDue> {
    if (getDataSource() === 'demo') {
      return {
        ...mockMaintenanceInsuranceDue,
        items: mockMaintenanceInsuranceDue.items.slice(0, limit),
      };
    }
    const headers = await this.buildHeaders();
    const response = await fetch(
      `${this.getBaseUrl()}/api/dashboard/widgets/asset-management/maintenance-insurance-due?limit=${limit}`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch maintenance and insurance due');
    return response.json();
  }

  async getAuditStatus(): Promise<AuditStatus> {
    if (getDataSource() === 'demo') {
      return getMockAuditStatus();
    }
    const headers = await this.buildHeaders();
    const response = await fetch(
      `${this.getBaseUrl()}/api/dashboard/widgets/asset-management/audit-status`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch audit status');
    return response.json();
  }

  async getExceptionAlerts(limit: number = 10): Promise<ExceptionAlerts> {
    if (getDataSource() === 'demo') {
      return getMockExceptionAlerts(limit);
    }
    const headers = await this.buildHeaders();
    const response = await fetch(
      `${this.getBaseUrl()}/api/dashboard/widgets/asset-management/exception-alerts?limit=${limit}`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch exception alerts');
    return response.json();
  }

  async getLifecycleStage(): Promise<LifecycleStage> {
    if (getDataSource() === 'demo') {
      return mockLifecycleStage;
    }
    const headers = await this.buildHeaders();
    const response = await fetch(
      `${this.getBaseUrl()}/api/dashboard/widgets/asset-management/lifecycle-stage`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch lifecycle stage');
    return response.json();
  }

  async getAssetAgeAnalysis(): Promise<AssetAgeAnalysis> {
    if (getDataSource() === 'demo') {
      return mockAssetAgeAnalysis;
    }
    const headers = await this.buildHeaders();
    const response = await fetch(
      `${this.getBaseUrl()}/api/dashboard/widgets/asset-management/age-analysis`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch asset age analysis');
    return response.json();
  }

  async getRecentAssetActivity(limit: number = 20): Promise<RecentAssetActivity> {
    if (getDataSource() === 'demo') {
      return {
        ...mockRecentAssetActivity,
        activities: mockRecentAssetActivity.activities.slice(0, limit),
      };
    }
    const headers = await this.buildHeaders();
    const response = await fetch(
      `${this.getBaseUrl()}/api/dashboard/widgets/asset-management/recent-activity?limit=${limit}`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch recent asset activity');
    return response.json();
  }
}

// Create a singleton instance
export const assetManagementWidgetsService = new AssetManagementWidgetsService();
